import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useAction } from '../components/toast'
import { Badge, Card, Kpi, Loaded, Modal, Notice, Page, Table } from '../components/ui'
import type { Column } from '../components/ui'
import { get, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { dateTime, minor } from '../lib/format'
import { useResource } from '../lib/useResource'

type Payout = {
  id: string
  coach_id: string
  coach_name: string
  period_start: string
  period_end: string
  gross_minor: number
  fee_minor: number
  net_minor: number
  currency: string
  status: string
  paid_at: string | null
  destination_masked: string
}

type PayoutList = { items: Payout[]; outstanding_minor: number }

type Gateway = { gateway: string; currencies: string[]; ready: boolean }

type PaymentEvent = {
  id: string
  gateway: string
  event_id: string
  event_type: string
  subscription_id: string | null
  received_at: string | null
  processed_at: string | null
  error: string
}

export function Payouts() {
  const { permissions } = useAuth()
  const canPay = permissions.has(P.billingWrite)
  const run = useAction()

  const [paying, setPaying] = useState<Payout | null>(null)
  const [unmatchedOnly, setUnmatchedOnly] = useState(false)

  const payouts = useResource((signal) => get<PayoutList>('/v1/admin/payouts', undefined, signal))
  const gateways = useResource((signal) =>
    get<Gateway[]>('/v1/admin/payments/gateways', undefined, signal),
  )
  const events = useResource(
    (signal) =>
      get<PaymentEvent[]>(
        '/v1/admin/payments/events',
        { unmatched_only: unmatchedOnly || undefined },
        signal,
      ),
    [unmatchedOnly],
  )

  const columns: Column<Payout>[] = [
    {
      key: 'coach',
      header: 'Coach',
      strong: true,
      render: (p) => <Link to={`/coaches/${p.coach_id}`}>{p.coach_name || p.coach_id}</Link>,
    },
    {
      key: 'period',
      header: 'Period',
      render: (p) => `${p.period_start} – ${p.period_end}`,
    },
    { key: 'gross', header: 'Gross', render: (p) => minor(p.gross_minor, p.currency) },
    { key: 'fee', header: 'SEYON kept', render: (p) => minor(p.fee_minor, p.currency) },
    {
      key: 'net',
      header: 'Owed',
      strong: true,
      render: (p) => minor(p.net_minor, p.currency),
    },
    {
      key: 'dest',
      header: 'To',
      render: (p) => <span className="mono">{p.destination_masked || '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) =>
        p.status === 'paid' ? (
          <Badge tone="green">Paid {p.paid_at ? dateTime(p.paid_at) : ''}</Badge>
        ) : (
          <Badge tone="orange">Outstanding</Badge>
        ),
    },
    {
      key: 'act',
      header: '',
      render: (p) =>
        canPay && p.status !== 'paid' ? (
          <button className="btn btn--sm btn--primary" onClick={() => setPaying(p)}>
            Mark paid…
          </button>
        ) : null,
    },
  ]

  const eventColumns: Column<PaymentEvent>[] = [
    { key: 'gateway', header: 'Gateway', render: (e) => e.gateway },
    { key: 'type', header: 'Event', strong: true, render: (e) => e.event_type },
    { key: 'id', header: 'Delivery id', render: (e) => <span className="mono">{e.event_id}</span> },
    {
      key: 'matched',
      header: 'Matched',
      render: (e) =>
        e.subscription_id ? (
          <span className="mono">{e.subscription_id}</span>
        ) : (
          <Badge tone="alert">{e.error || 'no subscription'}</Badge>
        ),
    },
    { key: 'received', header: 'Received', render: (e) => dateTime(e.received_at) },
    {
      key: 'processed',
      header: 'Processed',
      render: (e) => (e.processed_at ? dateTime(e.processed_at) : <Badge tone="orange">—</Badge>),
    },
  ]

  return (
    <Page
      title="Coach payouts"
      subtitle="What SEYON owes each coach for a closed period, and whether the money has left."
    >
      <Notice>
        <strong>These amounts are read, never recalculated.</strong> Gross, fee and net were written
        when the period closed. Marking a payout paid records that a transfer happened — it does not
        restate the sum, so this screen can never disagree with what the coach was actually owed.
      </Notice>

      <Loaded resource={gateways}>
        {(rows) => (
          <div className="grid grid--kpi">
            <Kpi
              label="Outstanding"
              value={minor(payouts.data?.outstanding_minor ?? 0)}
              tone={(payouts.data?.outstanding_minor ?? 0) > 0 ? 'orange' : 'green'}
              foot="across the periods listed below"
            />
            {rows.map((row) => (
              <Kpi
                key={row.gateway}
                label={`${row.gateway} gateway`}
                value={row.ready ? 'Ready' : 'Not configured'}
                tone={row.ready ? 'green' : 'alert'}
                foot={row.currencies.join(', ')}
              />
            ))}
          </div>
        )}
      </Loaded>

      {gateways.data?.some((g) => !g.ready) && (
        <Notice tone="warn">
          A gateway with no keys cannot take a payment. If coaches are priced in a currency that
          routes to it, the marketplace is a shop with the lights on and no till — subscribe will
          fail at the checkout step, after the client has chosen a plan.
        </Notice>
      )}

      <Card title="Periods" hint="Newest first">
        <Loaded
          resource={payouts}
          emptyWhen={(d) => d.items.length === 0}
          empty="No payout periods have closed yet."
        >
          {(data) => <Table columns={columns} rows={data.items} rowKey={(p) => p.id} />}
        </Loaded>
      </Card>

      <Card
        title="Webhooks"
        hint="Did the gateway's event arrive, and did it reach a subscription"
        action={
          <button
            className={unmatchedOnly ? 'btn btn--sm btn--primary' : 'btn btn--sm btn--ghost'}
            onClick={() => setUnmatchedOnly((v) => !v)}
          >
            {unmatchedOnly ? 'Showing unmatched' : 'Show unmatched only'}
          </button>
        }
      >
        <Notice>
          The gateway's payload is not shown and not fetched. It carries the payer's name, email and
          card metadata, and this list exists to answer whether an event arrived and matched —
          which needs none of that.
        </Notice>
        <Loaded
          resource={events}
          emptyWhen={(rows) => rows.length === 0}
          empty={unmatchedOnly ? 'Every event found its subscription.' : 'No webhooks received yet.'}
        >
          {(rows) => <Table columns={eventColumns} rows={rows} rowKey={(e) => e.id} />}
        </Loaded>
      </Card>

      {paying && (
        <MarkPaidDialog
          payout={paying}
          onClose={() => setPaying(null)}
          onConfirm={(reference) =>
            run(
              () => post(`/v1/admin/payouts/${paying.id}/mark-paid`, { reference }),
              `${paying.coach_name} marked paid`,
              payouts.reload,
            )
          }
        />
      )}
    </Page>
  )
}

/**
 * Not a `ReasonDialog`: what this needs is the bank's transfer reference, not a
 * free-text justification. "We paid them" with nothing to look it up by is not
 * a record, and this is the row a coach will be shown when they ask where their
 * money is.
 */
function MarkPaidDialog({
  payout,
  onConfirm,
  onClose,
}: {
  payout: Payout
  onConfirm: (reference: string) => Promise<void> | void
  onClose: () => void
}) {
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      await onConfirm(reference.trim())
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={`Mark ${payout.coach_name} paid`} onClose={onClose}>
      <div className="stack">
        <p className="muted" style={{ margin: 0 }}>
          {minor(payout.net_minor, payout.currency)} for {payout.period_start} –{' '}
          {payout.period_end}
          {payout.destination_masked ? ` to ${payout.destination_masked}` : ''}.
        </p>
        <div className="field">
          <label className="field__label" htmlFor="reference">
            Bank transfer reference
          </label>
          <input
            id="reference"
            className="input"
            autoFocus
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="NEFT / UTR / wire reference"
          />
        </div>
        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={busy || reference.trim().length < 3}
            onClick={() => void submit()}
          >
            {busy ? 'Working…' : 'Record as paid'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
